import { useState } from "react";

import type { CheckState, ParsedTsr } from "../lib/parse";
import type { VerificationResult } from "../lib/verify";

const STATE_ICON: Record<CheckState, string> = {
  pass: "✓",
  fail: "✕",
  skip: "—",
};

function Row({
  label,
  state,
  detail,
}: {
  label: string;
  state: CheckState;
  detail: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`verdict-row state-${state}`}>
      <button
        type="button"
        className="verdict-row-head"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="verdict-icon" aria-hidden>
          {STATE_ICON[state]}
        </span>
        <span className="verdict-label">{label}</span>
        <span className="verdict-state">{state}</span>
      </button>
      {open && <p className="verdict-detail">{detail}</p>}
    </div>
  );
}

export function VerdictBanner({
  parsed,
  verification,
  docName,
}: {
  parsed: ParsedTsr;
  verification: VerificationResult;
  docName: string | null;
}) {
  // Rejection responses get their own banner — there is no token to verify.
  if (parsed.status && parsed.status.code >= 2) {
    return (
      <section className="verdict verdict-rejected" aria-live="polite">
        <h2>
          The TSA refused this request: <em>{parsed.status.text}</em>
        </h2>
        {parsed.status.failInfo && <p>{parsed.status.failInfo}</p>}
        {parsed.status.statusStrings.map((s) => (
          <p key={s} className="muted">
            “{s}”
          </p>
        ))}
      </section>
    );
  }

  const checks = [verification.signature, verification.chain, verification.document];
  const overall: "green" | "amber" | "red" = checks.some(
    (c) => c.state === "fail"
  )
    ? "red"
    : checks.some((c) => c.state === "skip")
      ? "amber"
      : "green";

  const genTime = parsed.tstInfo?.genTime;
  const tsa =
    verification.chain.trustedRootSubject ??
    parsed.tstInfo?.tsaName ??
    "an unverified authority";

  return (
    <section className={`verdict verdict-${overall}`} aria-live="polite">
      <h2>
        {overall === "red"
          ? "This timestamp does not verify"
          : overall === "amber"
            ? "Timestamp partially verified"
            : docName
              ? `${docName} existed by ${fmtUtc(genTime)}`
              : `Timestamped ${fmtUtc(genTime)}`}
      </h2>
      <p className="verdict-sub">
        {overall !== "red" && (
          <>
            Attested by <strong>{tsa}</strong>. This proves the authority saw
            this hash at this time — not that the document's contents are true.
          </>
        )}
      </p>
      <div className="verdict-rows">
        <Row
          label="Signature valid"
          state={verification.signature.state}
          detail={verification.signature.detail}
        />
        <Row
          label="Chain trusted"
          state={verification.chain.state}
          detail={verification.chain.detail}
        />
        <Row
          label="Document matches digest"
          state={verification.document.state}
          detail={verification.document.detail}
        />
      </div>
    </section>
  );
}

function fmtUtc(iso?: string): string {
  if (!iso) return "an unknown time";
  return `${iso.replace("T", " ").replace(/\.\d+Z$/, " UTC").replace("Z", " UTC")}`;
}
